import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { getUsers, updateUser, deleteUser } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Users as UsersIcon, Trash, Pencil, Key } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = process.env.REACT_APP_BACKEND_URL;

const roleColors = {
  admin: 'bg-red-100 text-red-800',
  manager: 'bg-blue-100 text-blue-800',
  picker: 'bg-green-100 text-green-800',
  packer: 'bg-purple-100 text-purple-800',
  viewer: 'bg-muted text-slate-800',
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  // iter148 — admin password reset
  const [resetUser, setResetUser] = useState(null);
  const [resetMode, setResetMode] = useState('email'); // 'email' | 'temp'
  const [tempPw, setTempPw] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  const fetchData = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditName(user.name);
    setEditRole(user.role);
  };

  // iter148 — admin password reset
  const openReset = (user) => {
    setResetUser(user);
    setResetMode('email');
    setTempPw('');
  };

  const submitReset = async () => {
    if (!resetUser) return;
    if (resetMode === 'temp') {
      if (tempPw.length < 8 || !/[A-Za-z]/.test(tempPw) || !/\d/.test(tempPw)) {
        toast.error('Temp password must be 8+ chars with at least one letter and one digit.');
        return;
      }
    }
    setResetBusy(true);
    try {
      const body = resetMode === 'email'
        ? { send_email: true }
        : { new_password: tempPw };
      const r = await axios.post(
        `${API}/api/admin/users/${resetUser.id}/reset-password`,
        body,
        { withCredentials: true },
      );
      if (r.data.mode === 'email_sent') {
        toast.success(`Reset email sent to ${r.data.to}`);
      } else {
        toast.success('Temp password set. The user must sign in with the new password — all their existing sessions are invalidated.');
      }
      setResetUser(null);
      setTempPw('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reset password');
    } finally {
      setResetBusy(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateUser(editingUser.id, { name: editName, role: editRole });
      setEditingUser(null);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Delete this user?')) {
      try {
        await deleteUser(userId);
        fetchData();
      } catch (e) {
        console.error(e);
      }
    }
  };

  return (
    <Layout>
      <div data-testid="users-page" className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">Manage system users and roles</p>
        </div>

        <PendingInvites />

        <div className="bg-card border border-border rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full table-dense">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No users found</td></tr>
                ) : users.map(user => (
                  <tr key={user.id}>
                    <td className="font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-200 flex items-center justify-center font-bold text-sm">
                          {user.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        {user.name}
                      </div>
                    </td>
                    <td className="text-muted-foreground">{user.email}</td>
                    <td>
                      <span className={`px-2 py-1 text-xs font-bold uppercase ${roleColors[user.role] || 'bg-muted'}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="text-sm text-muted-foreground">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td>
                      {user.id !== currentUser?.id && (
                        <div className="flex gap-1">
                          <button className="p-1.5 hover:bg-muted" title="Edit" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                            <Pencil size={16} />
                          </button>
                          <button className="p-1.5 hover:bg-amber-50 text-amber-700" title="Reset password" onClick={() => openReset(user)} data-testid={`reset-pw-${user.id}`}>
                            <Key size={16} />
                          </button>
                          <button className="p-1.5 hover:bg-red-50 text-red-600" title="Delete" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                            <Trash size={16} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
          <DialogContent className="border border-border rounded-none">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="overline">Name</Label>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1" />
              </div>
              <div>
                <Label className="overline">Role</Label>
                <Select value={editRole} onValueChange={setEditRole}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="picker">Picker</SelectItem>
                    <SelectItem value="packer">Packer</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleSave} className="w-full bg-brand hover:bg-brand/90">Save Changes</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* iter148 — Admin reset-password dialog */}
        <Dialog open={!!resetUser} onOpenChange={(v) => { if (!v) setResetUser(null); }}>
          <DialogContent className="border border-border rounded-none" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Key size={18} weight="fill" /> Reset password</DialogTitle>
              <DialogDescription className="text-xs">
                Reset the password for <b>{resetUser?.email}</b>. Either email them a reset link (recommended) or set a temporary password they must change after signing in.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="inline-flex border border-border w-full">
                {[['email', 'Email link'], ['temp', 'Set temp password']].map(([k, lab]) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setResetMode(k)}
                    className={`flex-1 text-xs px-3 py-2 uppercase font-bold tracking-widest border-r border-border last:border-r-0 ${resetMode === k ? 'bg-brand text-white' : 'bg-background text-muted-foreground hover:bg-muted/50'}`}
                    data-testid={`reset-mode-${k}`}
                  >{lab}</button>
                ))}
              </div>
              {resetMode === 'email' ? (
                <p className="text-xs text-muted-foreground leading-relaxed bg-emerald-50 border border-emerald-200 p-3">
                  We&apos;ll email <b>{resetUser?.email}</b> a one-time reset link valid for 60 minutes. They click it, choose a new password, and sign back in — all their existing sessions become invalid.
                </p>
              ) : (
                <div>
                  <Label className="overline" htmlFor="temp-pw">Temporary password</Label>
                  <Input
                    id="temp-pw"
                    type="text"
                    value={tempPw}
                    onChange={(e) => setTempPw(e.target.value)}
                    placeholder="At least 8 chars · letter + digit"
                    className="mt-1 font-mono"
                    data-testid="reset-temp-pw"
                    autoFocus
                  />
                  <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                    Read this out to the user offline — we don&apos;t email it. They&apos;ll be signed out of every device immediately and must use this password to sign back in.
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setResetUser(null)} disabled={resetBusy} className="rounded-none" data-testid="reset-cancel">Cancel</Button>
                <Button onClick={submitReset} disabled={resetBusy || (resetMode === 'temp' && !tempPw)} className="rounded-none bg-brand hover:bg-brand/90 text-white" data-testid="reset-submit">
                  {resetBusy ? '…' : resetMode === 'email' ? 'Send reset email' : 'Set temp password'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

// ============================================================================
// Pending team invites — view / copy link / revoke
// ============================================================================
import { Copy, Trash as TrashBin, UserPlus, PaperPlaneTilt } from '@phosphor-icons/react';

function PendingInvites() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newInv, setNewInv] = useState({ email: '', role: 'picker' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/onboarding/invites`, { withCredentials: true });
      setRows(data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyLink = (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Invite link copied to clipboard'));
  };
  const revoke = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await axios.delete(`${API}/api/onboarding/invites/${id}`, { withCredentials: true });
      toast.success('Invite revoked');
      load();
    } catch { toast.error('Failed to revoke'); }
  };
  const invite = async () => {
    if (!newInv.email.includes('@')) { toast.error('Enter a valid email'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/api/onboarding/invites`,
        { invites: [newInv], app_url: window.location.origin },
        { withCredentials: true });
      toast.success(`Invite sent to ${newInv.email}`);
      setNewInv({ email: '', role: 'picker' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  return (
    <div className="bg-card border border-border rounded-lg mb-6 p-5" data-testid="pending-invites">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <UserPlus size={18} weight="bold" className="text-brand" />
          Pending invites
          <span className="text-xs font-mono text-muted-foreground">({rows.length})</span>
        </h2>
        <button onClick={load} className="text-xs uppercase tracking-widest text-muted-foreground font-mono hover:text-brand">Refresh</button>
      </div>

      {/* Quick-invite */}
      <div className="flex gap-2 mb-4 pb-4 border-b border-border">
        <Input
          data-testid="pi-quick-email"
          placeholder="email@company.com"
          value={newInv.email}
          onChange={(e) => setNewInv({ ...newInv, email: e.target.value })}
          className="flex-1"
        />
        <select
          data-testid="pi-quick-role"
          value={newInv.role}
          onChange={(e) => setNewInv({ ...newInv, role: e.target.value })}
          className="border border-border py-2 px-3 text-sm font-mono uppercase"
        >
          <option value="admin">Admin</option>
          <option value="manager">Manager</option>
          <option value="picker">Picker</option>
          <option value="packer">Packer</option>
          <option value="viewer">Viewer</option>
        </select>
        <Button data-testid="pi-quick-send" onClick={invite} disabled={creating} className="bg-brand hover:bg-brand/90 text-white rounded-none font-bold">
          <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
          {creating ? 'Sending…' : 'Invite'}
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground text-sm text-center py-6 font-mono uppercase tracking-widest">No pending invites</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
            <tr>
              <th className="text-left py-2">Email</th>
              <th className="text-left py-2">Role</th>
              <th className="text-left py-2">Expires</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border hover:bg-muted" data-testid={`pi-row-${r.email}`}>
                <td className="py-2">{r.email}</td>
                <td className="py-2"><span className={`text-[10px] px-2 py-0.5 font-mono uppercase ${roleColors[r.role] || 'bg-muted'}`}>{r.role}</span></td>
                <td className="py-2 text-xs text-muted-foreground font-mono">{r.expires_at ? r.expires_at.slice(0, 10) : '—'}</td>
                <td className="py-2 text-right">
                  <button
                    data-testid={`pi-copy-${r.email}`}
                    onClick={() => copyLink(r.token)}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-brand font-bold hover:underline mr-3"
                  >
                    <Copy size={12} weight="bold" /> Copy link
                  </button>
                  <button
                    data-testid={`pi-revoke-${r.email}`}
                    onClick={() => revoke(r.id)}
                    className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-rose-600 font-bold hover:underline"
                  >
                    <TrashBin size={12} weight="bold" /> Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

